import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

// ── Tipos ────────────────────────────────────────────────────────────────

export interface DiarioClasse {
  id: string;
  chamadaId: string;
  professorId: string;
  turmaId: string;
  materiaId: string;
  dataReferencia: string;
  conteudoMinistrado: string;
  metodologia?: string;
  recursosUtilizados?: string;
  observacoes?: string;
  objetivosCumpridos?: boolean;
  criadoEm: string;
  atualizadoEm: string;
  chamada?: {
    id: string;
    status: string;
    totalAlunos?: number;
    totalPresentes?: number;
    totalAusentes?: number;
    totalAtrasados?: number;
    concluidaEm?: string;
  };
  turma?: { id: string; nome: string };
  materia?: { id: string; nome: string };
  professor?: { id: string; nome: string };
  /** Calculado no backend: professor só edita dentro da janela (7 dias). */
  editavel?: boolean;
}

export interface CreateDiarioClasseInput {
  chamadaId: string;
  professorId: string;
  turmaId: string;
  materiaId: string;
  dataReferencia: string;
  conteudoMinistrado: string;
  metodologia?: string;
  recursosUtilizados?: string;
  observacoes?: string;
  objetivosCumpridos?: boolean;
}

export interface UpdateDiarioClasseInput {
  conteudoMinistrado?: string;
  metodologia?: string;
  recursosUtilizados?: string;
  observacoes?: string;
  objetivosCumpridos?: boolean;
}

// ── Hook ─────────────────────────────────────────────────────────────────

const BASE = '/diarios-classe';
const QK = 'diarios-classe';

/**
 * Hook para gerenciar Diários de Classe (Fase 2).
 *
 * @param filtros - Filtros para listar diários
 */
export function useDiarioClasse(filtros?: {
  turmaId?: string;
  materiaId?: string;
  professorId?: string;
  dataInicio?: string;
  dataFim?: string;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QK, filtros],
    queryFn: async () => {
      const { data } = await api.get<DiarioClasse[]>(BASE, { params: filtros });
      return Array.isArray(data) ? data : [];
    },
  });

  /** Buscar diário vinculado a uma chamada (1:1) */
  const useByChamada = (chamadaId: string | undefined) =>
    useQuery({
      queryKey: [QK, 'chamada', chamadaId],
      queryFn: async () => {
        if (!chamadaId) return null;
        const { data } = await api.get<DiarioClasse | null>(`${BASE}/chamada/${chamadaId}`);
        return data;
      },
      enabled: !!chamadaId,
    });

  const criar = useMutation({
    mutationFn: (input: CreateDiarioClasseInput) => api.post(BASE, input).then(r => r.data as DiarioClasse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, ...input }: UpdateDiarioClasseInput & { id: string }) =>
      api.patch(`${BASE}/${id}`, input).then(r => r.data as DiarioClasse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  return {
    diarios: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    criar,
    atualizar,
    useByChamada,
  };
}
